if (!customElements.get("ext-product-details-drawer")) {
  class ExtProductDetailsDrawer extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    // T2.2 — init: cache elements, wire close + backdrop + Tab focus-trap + Escape
    init() {
      this.toggleState = false;
      this._lastFocused = null;
      this._sliderInstance = null;

      if (!this.classList.contains("is--open")) {
        document.querySelector("body").classList.remove("overflow-hidden");
      }

      this.querySelector(".button--close").addEventListener(
        "click",
        this.close.bind(this)
      );

      this.querySelector(
        ".ext-product-details-drawer__backdrop"
      ).addEventListener("click", this.close.bind(this));

      // Tab focus-trap + Escape — copied verbatim from quick-cart-drawer, selectors updated
      this.querySelector(".ext-product-details-drawer__blocks").addEventListener("keydown", e => {
        if (e.key === "Escape" && this.toggleState) {
          this.close();
        }

        if (this.toggleState) {
          const focusableElements = this.querySelectorAll(
            'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement =
            focusableElements[focusableElements.length - 1];

          const isTabPressed = e.key === "Tab";
          if (!isTabPressed) return;

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault();
              lastFocusableElement.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        }
      });
    }

    // T2.3 — open(url, stepIndex): fetch, inject, wire, reveal
    async open(url, stepIndex) {
      // Validate stepIndex — no silent fallback to 0 (REQ-07)
      const parsedStepIndex = Number(stepIndex);
      if (stepIndex === null || stepIndex === undefined || isNaN(parsedStepIndex)) {
        console.error(
          "[ext-product-details-drawer] open() called with invalid stepIndex:",
          stepIndex,
          "— drawer will NOT open."
        );
        return;
      }

      this._lastFocused = document.activeElement;

      let productFragment;
      try {
        const response = await fetch(`${url}?view=ext-product-details-drawer`);
        if (!response.ok) {
          console.error("[ext-product-details-drawer] fetch failed:", response.status, url);
          return;
        }
        const html = await response.text();
        const parser = document.createElement("div");
        parser.insertAdjacentHTML("beforeend", html);
        productFragment = parser.querySelector(".ext-product-details-drawer-product");
        if (!productFragment) {
          console.error("[ext-product-details-drawer] .ext-product-details-drawer-product not found in response.");
          return;
        }
      } catch (err) {
        console.error("[ext-product-details-drawer] fetch error:", err);
        return;
      }

      // Clear + inject into main content slot
      const mainSlot = this.querySelector(".ext-product-details-drawer__main");
      mainSlot.innerHTML = "";
      mainSlot.append(productFragment);

      // Stamp stepIndex onto the button (REQ-04 step 4)
      const addBtn = mainSlot.querySelector(".ext-product-details-drawer__add");
      if (addBtn) {
        addBtn.dataset.stepIndex = String(parsedStepIndex);
      }

      // Wire gallery, variant sync, add-button click
      this._initGallery();
      this._wireVariantSync();
      this._wireAddButton();

      // Reveal drawer (REQ-04 steps 7–9)
      this.toggleState = true;
      this.classList.add("is--open");
      document.querySelector("body").classList.add("overflow-hidden");

      // Focus first focusable element
      const firstFocusable = this.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }

    // T2.4 — gallery init: Swiper with same config as quick-cart-drawer
    _initGallery() {
      if (this._sliderInstance) {
        try { this._sliderInstance.destroy(true, true); } catch (_) {}
        this._sliderInstance = null;
      }

      const swiperEl = this.querySelector(".ext-product-details-drawer__media-swiper");
      if (!swiperEl) return;

      this._sliderInstance = new Swiper(".ext-product-details-drawer__media-swiper", {
        slidesPerView: 1.25,
        spaceBetween: 8,
        freeMode: {
          enabled: true
        },
        breakpoints: {
          750: {
            slidesPerView: 2
          }
        }
      });
    }

    // T2.5 — variant sync: resolve from [data-product-variants-json] directly (AMENDMENT)
    _wireVariantSync() {
      const radios = this.querySelectorAll(".variant-option-radio-input");
      if (!radios.length) return; // variant-less guard

      radios.forEach(radio => {
        radio.addEventListener("change", () => {
          this._resolveAndSyncVariant();
        });
      });
    }

    _resolveAndSyncVariant() {
      const mainSlot = this.querySelector(".ext-product-details-drawer__main");
      const variantsScript = mainSlot.querySelector("[data-product-variants-json]");
      if (!variantsScript) return;

      let variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error("[ext-product-details-drawer] failed to parse variants JSON:", e);
        return;
      }

      // Read the checked value for each option position (option 1 / 2 / 3 by name suffix)
      // Radio names are: {productId}-ext-option[1], [2], [3]
      const checkedByPosition = {};
      mainSlot.querySelectorAll(".variant-option-radio-input:checked").forEach(radio => {
        // Extract option position from name: e.g. "123456-ext-option[2]" → 2
        const match = radio.name.match(/\[(\d+)\]$/);
        if (match) {
          checkedByPosition[Number(match[1])] = radio.value;
        }
      });

      // Match against variant option1/option2/option3
      const matched = variants.find(v => {
        const opt1Match = !checkedByPosition[1] || v.option1 === checkedByPosition[1];
        const opt2Match = !checkedByPosition[2] || v.option2 === checkedByPosition[2];
        const opt3Match = !checkedByPosition[3] || v.option3 === checkedByPosition[3];
        return opt1Match && opt2Match && opt3Match;
      });

      if (!matched) return;

      const addBtn = mainSlot.querySelector(".ext-product-details-drawer__add");
      if (addBtn) {
        addBtn.dataset.variantId = String(matched.id);
        addBtn.dataset.productPrice = String(matched.price);
        if (matched.sku !== undefined) addBtn.dataset.productSku = String(matched.sku);
        if (matched.featured_image) addBtn.dataset.productImage = matched.featured_image.src || "";
      }

      // Navigate gallery to featured media
      if (matched.featured_media) {
        this.setActiveMedia(matched.featured_media.id);
      }

      // T2.6 — enforce availability
      this._enforceAvailability(matched);
    }

    // T2.6 — availability enforcement
    _enforceAvailability(variant) {
      const addBtn = this.querySelector(
        ".ext-product-details-drawer__main .ext-product-details-drawer__add"
      );
      if (!addBtn) return;

      if (!variant.available) {
        addBtn.disabled = true;
        addBtn.setAttribute("aria-disabled", "true");
        // Re-use product.unavailable i18n semantics (label is already on the server-rendered btn
        // for OOS default; here we set it dynamically for runtime selection change)
        if (!addBtn.getAttribute("aria-label")) {
          addBtn.setAttribute("aria-label", addBtn.getAttribute("data-unavailable-label") || "Unavailable");
        }
      } else {
        addBtn.disabled = false;
        addBtn.setAttribute("aria-disabled", "false");
        addBtn.removeAttribute("aria-label");
      }
    }

    // Wire the add-button click inside the injected content
    _wireAddButton() {
      const addBtn = this.querySelector(
        ".ext-product-details-drawer__main .ext-product-details-drawer__add"
      );
      if (!addBtn) return;

      // Remove any previous listener (safety for re-open)
      addBtn.removeEventListener("click", this._boundOnAddToRoutine);
      this._boundOnAddToRoutine = this._onAddToRoutine.bind(this);
      addBtn.addEventListener("click", this._boundOnAddToRoutine);
    }

    // T2.7 — add handler: build productData, dispatch custom event, close
    _onAddToRoutine(e) {
      e.preventDefault();

      const addBtn = e.currentTarget;
      const mainSlot = this.querySelector(".ext-product-details-drawer__main");

      // Read title from the h2 inside the injected snippet
      const titleEl = mainSlot.querySelector(".ext-product-details-drawer__title");

      const productData = {
        id: addBtn.dataset.productId,
        variantId: addBtn.dataset.variantId,
        title: titleEl ? titleEl.textContent.trim() : "",
        url: addBtn.dataset.productUrl,
        sku: addBtn.dataset.productSku,
        // `image` is the field _fillSlot renders into <img>; keep `imageUrl` for the slot dataset
        image: addBtn.dataset.productImage,
        imageUrl: addBtn.dataset.productImage,
        // data-product-price is raw cents; the summary sums price as currency units (parseFloat)
        price: parseFloat(addBtn.dataset.productPrice || "0") / 100,
        stepIndex: addBtn.dataset.stepIndex,
        // unique id so removal linkage matches the quick-add path
        selectId: `${addBtn.dataset.productId}-${Date.now()}`
      };

      // Guard: all 7 fields must be non-empty (REQ-02)
      const missing = Object.entries(productData)
        .filter(([, v]) => v === undefined || v === null || v === "")
        .map(([k]) => k);

      if (missing.length) {
        console.error(
          "[ext-product-details-drawer] productData has empty fields:",
          missing,
          productData
        );
        return;
      }

      document.dispatchEvent(
        new CustomEvent("ext:add-to-routine", {
          bubbles: true,
          detail: productData
        })
      );

      this.close();
    }

    // T2.8 — setActiveMedia: copied from quick-cart-drawer, class updated
    setActiveMedia(id) {
      const mediaFound = Array.from(
        this.querySelectorAll("[data-media-id]")
      ).find(media => Number(media.dataset.mediaId) === id);
      if (mediaFound && this._sliderInstance) {
        this._sliderInstance.slideTo(Number(mediaFound.dataset.index));
      }
    }

    // T2.8 — close: remove is--open, release body lock, restore focus
    // DROP: card-product-slider autoplay coupling (not relevant here)
    close() {
      this.toggleState = false;
      document.querySelector("body").classList.remove("overflow-hidden");
      this.classList.remove("is--open");

      if (this._lastFocused && typeof this._lastFocused.focus === "function") {
        this._lastFocused.focus();
      }
      this._lastFocused = null;
    }
  }

  customElements.define("ext-product-details-drawer", ExtProductDetailsDrawer);
}
